import type { SortingState } from "@tanstack/react-table";
import type {
  AgingBucket,
  InvoiceTypeFilter,
  EscalationFilter,
  StatusFilters,
  ViewBy,
  EmailSentByFilter,
  RiskLevelFilters,
} from "../components/receivables/types";
import type { IntentionStatus } from "./types";

export const RECEIVABLES_TABLE_STATE_KEY = "receivables-table-state";

export type ReceivablesTableStateSnapshot = {
  search: string;
  viewBy: ViewBy;
  riskLevels: RiskLevelFilters;
  invoiceTypeFilter: InvoiceTypeFilter;
  escalationFilter: EscalationFilter;
  statusFilters: StatusFilters;
  sorting: SortingState;
  page: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  filterCollectorId: number | "";
  filterGroupId: number | "";
  filterSentBy: EmailSentByFilter;
};

const VALID_PAGE_SIZES = [10, 15, 25, 50] as const;

const VIEW_BY_VALUES: ViewBy[] = ["customers", "invoices", "emails"];
const AGING_BUCKET_VALUES: AgingBucket[] = [
  "current",
  "0_30",
  "31_60",
  "61_90",
  "90_plus",
];
const LEGACY_RISK_LEVEL_VALUES = ["low", "medium", "high", "all"] as const;
const ESCALATION_FILTER_VALUES: EscalationFilter[] = [
  "all",
  "escalated",
];
const INVOICE_TYPE_FILTER_VALUES: InvoiceTypeFilter[] = ["all", "PI", "3RD"];
const INTENTION_STATUS_VALUES: IntentionStatus[] = [
  "Approved",
  "Unresolved",
  "No_reply",
  "Needs_attention",
  "In_progress",
];

function isViewBy(value: unknown): value is ViewBy {
  return typeof value === "string" && VIEW_BY_VALUES.includes(value as ViewBy);
}

function parseRiskLevels(value: unknown, legacyRiskLevel: unknown): RiskLevelFilters | null {
  if (Array.isArray(value)) {
    const parsed: AgingBucket[] = [];
    for (const item of value) {
      if (
        typeof item === "string" &&
        AGING_BUCKET_VALUES.includes(item as AgingBucket) &&
        !parsed.includes(item as AgingBucket)
      ) {
        parsed.push(item as AgingBucket);
      } else if (item !== undefined && item !== null) {
        return null;
      }
    }
    return parsed;
  }

  if (typeof value === "string") {
    return AGING_BUCKET_VALUES.includes(value as AgingBucket)
      ? [value as AgingBucket]
      : null;
  }

  if (typeof legacyRiskLevel === "string") {
    if (legacyRiskLevel === "all") {
      return [];
    }
    if (AGING_BUCKET_VALUES.includes(legacyRiskLevel as AgingBucket)) {
      return [legacyRiskLevel as AgingBucket];
    }
    if (
      LEGACY_RISK_LEVEL_VALUES.includes(
        legacyRiskLevel as (typeof LEGACY_RISK_LEVEL_VALUES)[number],
      )
    ) {
      return [];
    }
  }

  if (value === undefined && legacyRiskLevel === undefined) {
    return [];
  }

  return null;
}

function isInvoiceTypeFilter(value: unknown): value is InvoiceTypeFilter {
  return (
    typeof value === "string" &&
    INVOICE_TYPE_FILTER_VALUES.includes(value as InvoiceTypeFilter)
  );
}

function normalizeEscalationFilter(value: unknown): EscalationFilter {
  if (
    typeof value === "string" &&
    ESCALATION_FILTER_VALUES.includes(value as EscalationFilter)
  ) {
    return value as EscalationFilter;
  }

  // pending_approval used to be persisted, but there is no visible control to clear it.
  return "all";
}

function parseStatusFilters(value: unknown): StatusFilters | null {
  if (value === "all" || value === undefined) {
    return [];
  }
  if (typeof value === "string") {
    return INTENTION_STATUS_VALUES.includes(value as IntentionStatus)
      ? [value as IntentionStatus]
      : null;
  }
  if (!Array.isArray(value)) {
    return null;
  }
  const parsed: IntentionStatus[] = [];
  for (const item of value) {
    if (
      typeof item === "string" &&
      INTENTION_STATUS_VALUES.includes(item as IntentionStatus) &&
      !parsed.includes(item as IntentionStatus)
    ) {
      parsed.push(item as IntentionStatus);
    } else if (item !== undefined && item !== null) {
      return null;
    }
  }
  return parsed;
}

function parseSorting(value: unknown): SortingState | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const parsed: SortingState = [];
  for (const item of value) {
    if (
      typeof item !== "object" ||
      item === null ||
      typeof (item as { id?: unknown }).id !== "string"
    ) {
      return null;
    }
    const { id, desc } = item as { id: string; desc?: unknown };
    parsed.push({ id, desc: Boolean(desc) });
  }
  return parsed;
}

function parsePositiveInt(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  const rounded = Math.floor(value);
  return rounded >= 1 ? rounded : null;
}

function parseManagerFilterId(value: unknown): number | "" | null {
  if (value === "") return "";
  if (typeof value === "number" && Number.isFinite(value) && value >= 1) {
    return Math.floor(value);
  }
  return null;
}

function parseSentByFilter(value: unknown): EmailSentByFilter | null {
  if (value === "") return "";
  if (value === "automated") return "automated";
  if (typeof value === "number" && Number.isFinite(value) && value >= 1) {
    return Math.floor(value);
  }
  return null;
}

function parseYmdDate(value: unknown): string | undefined {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return undefined;
  }
  return value;
}

function parsePageSize(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  const rounded = Math.floor(value);
  return VALID_PAGE_SIZES.includes(rounded as (typeof VALID_PAGE_SIZES)[number])
    ? rounded
    : undefined;
}

function parseSnapshot(raw: unknown): ReceivablesTableStateSnapshot | null {
  if (typeof raw !== "object" || raw === null) return null;
  const data = raw as Record<string, unknown>;

  if (!isViewBy(data.viewBy)) return null;
  const riskLevels = parseRiskLevels(data.riskLevels, data.riskLevel);
  if (riskLevels === null) return null;
  const statusFilters = parseStatusFilters(
    data.statusFilters ?? data.statusFilter,
  );
  if (statusFilters === null) return null;
  const viewBy = data.viewBy;
  const escalationFilter =
    viewBy === "invoices"
      ? normalizeEscalationFilter(data.escalationFilter)
      : "all";

  const sorting = parseSorting(data.sorting);
  if (!sorting) return null;

  const page = parsePositiveInt(data.page);
  if (page === null) return null;

  const filterCollectorId = parseManagerFilterId(data.filterCollectorId);
  if (filterCollectorId === null) return null;

  const filterGroupId = parseManagerFilterId(data.filterGroupId);
  if (filterGroupId === null) return null;

  const filterSentBy = parseSentByFilter(data.filterSentBy);
  if (filterSentBy === null) return null;

  const pageSize = parsePageSize(data.pageSize);

  return {
    search: typeof data.search === "string" ? data.search : "",
    viewBy,
    riskLevels,
    invoiceTypeFilter: isInvoiceTypeFilter(data.invoiceTypeFilter)
      ? data.invoiceTypeFilter
      : "all",
    escalationFilter,
    statusFilters,
    sorting,
    page,
    ...(pageSize !== undefined ? { pageSize } : {}),
    startDate: parseYmdDate(data.startDate),
    endDate: parseYmdDate(data.endDate),
    filterCollectorId,
    filterGroupId,
    filterSentBy,
  };
}

export function readReceivablesTableState(): ReceivablesTableStateSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.sessionStorage.getItem(RECEIVABLES_TABLE_STATE_KEY);
    if (!stored) return null;
    return parseSnapshot(JSON.parse(stored));
  } catch {
    return null;
  }
}

export function writeReceivablesTableState(
  snapshot: ReceivablesTableStateSnapshot,
): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      RECEIVABLES_TABLE_STATE_KEY,
      JSON.stringify(snapshot),
    );
  } catch {
    /* ignore */
  }
}
